import { trpc } from '@/main'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { reportSearchDeps, reportQueryOptions } from '@/hooks/use-report'
import { noop } from '@tanstack/react-query'

/**
 * Layout for a selected functional group (`/orgs/$orgId/groups/$groupId/*`). It renders the animated
 * tab Outlet (real content), so per AGENTS.md it OWNS the orgId/groupId-keyed chrome its subtree reads
 * — the groups list (`getGroups`), the group users list (`getOrgGroupUsers`, read by the users panel in
 * orgs.tsx), and the report data shared by all three tab children. The board/distribution leaves keep
 * only their own tab-specific query (`getOrgGroupProfile`); the report leaf has no loader.
 *
 * The `AnimatedOutlet` now lives in the parent `orgs.tsx` (wrapping the `<Outlet/>` that renders this
 * layout) so the enter animation plays once when the report panel first appears and never re-animates on
 * tab/group switches; this layout renders a plain `<Outlet/>` for its tab children.
 */
export const Route = createFileRoute('/_app/orgs/$orgId/groups/$groupId')({
  loaderDeps: ({ search }) => reportSearchDeps(search),
  context: ({ deps, params }) => ({
    reportQueryOptions: reportQueryOptions(deps, params),
    // Both are read ABOVE this route (the groups breadcrumb + group name in orgs.tsx, the users panel it
    // renders), but only this route has the ids — so it owns the prefetch and everyone shares the factory.
    orgGroupsQueryOptions: trpc.orgs.getGroups.queryOptions(params.orgId),
    orgGroupUsersQueryOptions: trpc.orgs.getOrgGroupUsers.queryOptions({
      orgId: params.orgId,
      groupId: params.groupId,
      orgType: deps.orgType,
    }),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.orgGroupsQueryOptions).catch(noop)
    context.queryClient.query(context.orgGroupUsersQueryOptions).catch(noop)
    if (context.reportQueryOptions) context.queryClient.query(context.reportQueryOptions).catch(noop)
  },
  component: RouteComponent,
  // No pendingComponent: this passthrough layout just renders <AnimatedOutlet/>. The data-cold case is
  // covered by the parent orgs.tsx (<Suspense fallback={ReportPanelSkeleton}>), and each tab leaf
  // (report/board/distribution) owns its own tab-specific skeleton — a generic one here would mismatch.
})

function RouteComponent() {
  return <Outlet />
}
